import { Component, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, Observable } from "rxjs";
import { map, startWith, take } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";
import { Store } from "@ngrx/store";
import {
  CHANGE_ACCOUNT,
  CHANGE_ACCOUNT_SUCCESS,
  ChangeAccountAction,
  FetchAvailableAccountsAction,
  FETCH_AVAILABLE_ACCOUNT_SUCCESS,
  FETCH_AVAILABLE_ACCOUNT_FAILURE,
} from "./accounts.actions";
import { Actions, ofType } from "@ngrx/effects";
import { AvailableAccount } from "./accounts.interface";
import { AccountsService } from "./accounts.service";
import { getAvailableAccountsSelector } from "./accounts.reducer";

@Component({
  selector: "app-accounts",
  templateUrl: "./accounts.component.html",
  styleUrls: ["./accounts.component.scss"],
  standalone: false,
})
export class AccountsComponent implements OnInit {
  accountForm = new FormControl("");
  filteredOptions$: Observable<AvailableAccount[]>;
  accounts$: Observable<AvailableAccount[]>;
  isLoading = true;
  selectingDomain: string | null = null;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private store: Store,
    private actions: Actions,
    private accountsService: AccountsService,
    private router: Router,
  ) {}

  ngOnInit() {
    if (!this.authService.canChangeAccounts) {
      void this.router.navigate(this.authService.getDefaultAppCommands());
      return;
    }

    this.store.dispatch(new FetchAvailableAccountsAction());

    this.accounts$ = this.store.select(getAvailableAccountsSelector);

    this.filteredOptions$ = combineLatest([
      this.accounts$,
      this.accountForm.valueChanges.pipe(startWith("")),
    ]).pipe(map(([accounts, form]) => this.filter(accounts, form)));

    this.actions
      .pipe(
        ofType(
          FETCH_AVAILABLE_ACCOUNT_SUCCESS,
          FETCH_AVAILABLE_ACCOUNT_FAILURE,
        ),
        take(1),
      )
      .subscribe(() => {
        this.isLoading = false;
      });

    this.actions
      .pipe(ofType(CHANGE_ACCOUNT, CHANGE_ACCOUNT_SUCCESS), take(1))
      .subscribe(() => {
        void this.navigateAfterAccountResolved();
      });
  }

  private async navigateAfterAccountResolved(): Promise<void> {
    try {
      await this.authService.fetchCurrent();
    } catch {
      return;
    }

    if (!this.authService.hasCompanyManagerAccess) {
      await this.router.navigate(["/my-account"]);
      return;
    }

    const onboardingState = (
      this.authService.appUser?.onboarding || ""
    ).toString();
    if (!["COMPLETED", "SKIPPED"].includes(onboardingState)) {
      const queryParams: Record<string, string> = { fromStart: "true" };
      const currentDomain = this.authService.currentDomain;
      if (currentDomain) {
        queryParams["domain"] = currentDomain;
      }
      await this.router.navigate(["/onboarding"], { queryParams });
      return;
    }

    await this.router.navigate(["/signatures", "custom"]);
  }

  private filter = (
    accounts: AvailableAccount[],
    value: string,
  ): AvailableAccount[] => {
    const filterValue = value.toLowerCase();

    return accounts.filter((option) => {
      const domain = option.domain?.toLowerCase();
      const name = option.name?.toLowerCase();

      return domain?.includes(filterValue) || name?.includes(filterValue);
    });
  };

  changeAccount(account: AvailableAccount) {
    if (this.selectingDomain) {
      return;
    }
    this.selectingDomain = account.domain;
    this.store.dispatch(new ChangeAccountAction(account.domain));
  }
}
